import type { AuthData } from '../AuthService';
import type { UnregisterCallback } from '../MessageManager';
import type { LockContext } from '@easterngraphics/wcf/modules/utils/async';

import { _sendReply, _fakeMessage } from '../Communication';
import { registerHandler } from '../MessageManager';

import { PCON_LOGIN_CLIENT_ID } from '@egr/xbox/pcon-login-api/Constants';
import { SettingProvider } from '@egr/xbox/pcon-login-api/SettingsProvider';
import { loadInDebugIframe } from '@egr/xbox/utils/Debug';
import { getCookie, setCookie } from '@egr/xbox/utils/debug/Cookies';
import { Logger } from '@egr/xbox/utils/errors/Logger';
import { getObjectFromQueryString } from '@egr/xbox/utils/string/url/getObjectFromQueryString';

import { ajax, Lock } from '@easterngraphics/wcf/modules/utils/async';
import { isNotNullOrEmpty, isNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

const [getLastToken, setLastToken] = ((): [() => string, (value: string) => void] => {
    const cookieKey = 'debug_auth_lastToken';
    let lastValue: string = getCookie(cookieKey);

    return [
        () => lastValue,
        (value: string) => {
            lastValue = value;
            setCookie(cookieKey, value, 3600);
        }
    ];
})();

const oauthTokenLock: Lock = new Lock();
const settings: SettingProvider = new SettingProvider({
    clientId: PCON_LOGIN_CLIENT_ID,
    callbackMode: 'postMessage'
});

function getAuthData(token: string): AuthData {
    if (isNotNullOrEmpty(token)) {
        return {
            valid: true,
            access_token: token,
            expires_in: 3599
        };
    }

    return {
        valid: false,
        access_token: '',
        expires_in: -1
    };
}

let closeIframe: VoidFunction | undefined;
async function login(event: MessageEvent): Promise<void> {
    if (event.data.arguments?.force === true) {
        await logout();
    }

    const lockContext: LockContext = await oauthTokenLock.acquireLock();
    const unregister: UnregisterCallback = registerHandler({
        type: 'oauth_token',
        callback: (loginEvent: MessageEvent): void => {
            unregister();
            lockContext.unlock();
            closeIframe?.();
            if (isNotNullOrEmpty(loginEvent.data.value)) {
                const data = getObjectFromQueryString(loginEvent.data.value);
                setLastToken(data.access_token);
                _sendReply(event.data.transaction_id, false, getAuthData(data.access_token));
            } else {
                _sendReply(event.data.transaction_id, true, 'login failed');
            }
        }
    });
    closeIframe = loadInDebugIframe(
        settings.authApiUrl,
        (): void => {
            _fakeMessage({
                transaction_id: -1,
                type: 'progress',
                action: 'auth_service',
                arguments: {
                    state: 'visible'
                }
            });
        }
    );
}

async function logout(): Promise<void> {
    const lastToken: string = getLastToken();
    if (isNullOrEmpty(lastToken)) {
        return;
    }

    const lockContext: LockContext = await oauthTokenLock.acquireLock();
    try {
        await ajax(
            'PUT',
            settings.logoutApiUrl,
            '{}', // Note: the pCon.login API is strange...
            {
                headers: {
                    Authorization: `Bearer ${lastToken}`,
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                withCredentials: true
            }
        );
    } catch (error) {
        // Note: the app api does not reply so we have to ensure that an error thrown will
        // not cause an error on the request side. Therefore we simply print and ignore the error.
        Logger.error(error);
    } finally {
        setLastToken('');
        lockContext.unlock();
    }
}

export function registerDebugAuthService(): void {
    registerHandler({
        type: 'auth_service',
        callback: async (event: MessageEvent): Promise<void> => {
            const lastToken: string = getLastToken();
            try {
                switch (event.data.action) {
                    case 'refresh':
                        if (isNullOrEmpty(lastToken) && event.data.arguments?.force === true) {
                            await login(event);
                        } else {
                            _sendReply(event.data.transaction_id, false, getAuthData(lastToken));
                        }
                        break;
                    case 'login':
                        await login(event);
                        break;
                    case 'logout':
                        await logout();
                        break;
                    case 'get_data':
                        _sendReply(event.data.transaction_id, false, getAuthData(lastToken));
                        break;
                    default:
                        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                        _sendReply(event.data.transaction_id, true, 'invalid action: ' + event.data.action);
                }
            } catch (error) {
                _sendReply(event.data.transaction_id, true, error instanceof Error ? error.message : error);
            }
        }
    });
}